/**
 * Media-query hook.
 *
 * `useSyncExternalStore` is used rather than a `useState`/`useEffect` pair so the value is correct on the very first
 * render (no flash of the wrong theme) and React re-subscribes reliably under StrictMode.
 */

import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribes to a CSS media query.
 *
 * @param query Media query such as `(prefers-color-scheme: dark)`.
 * @returns Whether the query currently matches.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onStoreChange);

      return () => list.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  // The server has no window; a `false` snapshot keeps the first paint deterministic (and jsdom safe).
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
